import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get } from 'lodash';
import { Map } from 'immutable';
import config from 'config';
import ReactTableComponent from 'components/ReactTableComponent';
import styles from 'modules/Profile/components/FamilyAccess/FamilyAccess.module.scss';
import FamilyAccessTitle from 'modules/Profile/components/FamilyAccess/FamilyAccessTitle';
import Button from 'components/Button';
import { ReactComponent as PlusIcon } from 'images/ic_add_circle.svg';
import Modal from 'components/Modal';
import AddTaxpayer from './AddTaxpayer';
import WardActions from './WardActions';
import DateFormats from 'constants/DateFormats';
import { createRelations, refuseRelations } from 'modules/Profile/services/API';
import { MESSAGE_ERROR } from 'constants/responseErrors';

const WardsList = ({
  fetchNextPage,
  onSortedChange,
  relations,
  canAdd,
  defaultSorted,
  updateRelation,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const onRemove = useCallback(async ({ inn }) => {
    const data = await refuseRelations({ inn });

    if (data.isOk) {
      // TODO: implement reducer update when BE is ready
      updateRelation();
    }
  }, []);

  const onAddTaxpayer = useCallback(async (inn) => {
    const data = await createRelations({ inn });

    if (data.isOk) {
      setModalVisible(false);
      // TODO: implement reducer update when BE is ready
      updateRelation();
    }
    if (data.error) {
      const errorType = data.error?.response?.data?.errorType;
      if (errorType && errorType === MESSAGE_ERROR) {
        updateRelation();
      }
    }

    return data;
  }, []);

  const paginationOptions = useMemo(
    () =>
      relations
        ? {
            number: relations.get('number'),
            size: relations.get('size'),
            sort: relations.get('sort'),
            totalPages: relations.get('totalPages'),
          }
        : {},
    [],
  );
  const columns = useMemo(
    () => [
      {
        Header: 'ИНН',
        accessor: 'inn',
        sortable: true,
        width: 180,
        className: styles.cell,
      },
      {
        Header: 'ФИО',
        accessor: 'fullName',
        sortable: true,
        className: styles.cell,
        Cell: FamilyAccessTitle,
      },
      {
        Header: 'Дата рождения',
        accessor: 'dateOfBirth',
        sortable: true,
        width: 200,
        className: styles.cell,
        Cell: ({ value }) =>
          moment(value).isValid() ? moment(value).format(DateFormats.long) : null,
      },
      {
        Header: 'Статус подписки',
        accessor: 'subscriptionStatus',
        sortable: true,
        width: 270,
        className: styles.cell,
        Cell: ({ value, original: { dateEnd, controlDate } }) =>
          moment(dateEnd).isValid()
            ? `${value} c ${moment(dateEnd).format(DateFormats.long)}`
            : moment(controlDate).isValid()
            ? `${value} до ${moment(controlDate).format(DateFormats.long)}`
            : value,
      },
      {
        Header: '',
        Cell: config.isInspectorArm ? null : WardActions,
        getProps: (
          tableState,
          { original: { inn, active, terminationReason, relationAvailable } },
        ) => ({
          inn,
          active,
          terminationReason,
          onRemove,
          relationAvailable,
        }),
        width: 80,
        className: styles.cell,
      },
    ],
    [],
  );

  const getClassName = useCallback(
    (_, { original: { active } }) => ({
      className: !active ? styles.rowDisabled : '',
    }),
    [],
  );

  const tableData = useMemo(
    () => (relations.get('content') ? relations.get('content').toJS() : []),
    [relations],
  );

  return (
    <div>
      <div className={styles.header}>
        <h2 className={'mb-0'}>Список несовершеннолетних детей</h2>
        <div className={'ml-md-auto ml-0'}>
          {!config.isInspectorArm && (
            <Button onClick={() => setModalVisible(true)}>
              <PlusIcon className={styles.addIcon} /> Добавить пользователя
            </Button>
          )}
        </div>
      </div>
      {!tableData.length && (
        <div className={styles.placeholder}>
          <span className={styles.text}>
            В данном разделе отображаются запросы на доступ к информации из Личного кабинета
            налогоплательщика несовершеннолетнего ребенка. При наличии подтвержденного запроса Вы
            получите возможность просматривать и оплачивать платёжные документы несовершеннолетнего
            ребенка, либо управлять списком, кому такая возможность уже предоставлена.
          </span>
        </div>
      )}

      {!!tableData.length && (
        <ReactTableComponent
          theme={'blue'}
          columns={columns}
          tableData={tableData}
          paginationOptions={get(paginationOptions, 'totalPages', 0) > 1 ? paginationOptions : null}
          fetchNextPage={fetchNextPage}
          onSortedChange={onSortedChange}
          getTrProps={getClassName}
          defaultSorted={defaultSorted}
        />
      )}

      <Modal showModal={modalVisible} type={'small'} onClose={() => setModalVisible(false)}>
        {canAdd ? (
          <AddTaxpayer onSubmit={onAddTaxpayer} onClose={() => setModalVisible(false)} />
        ) : (
          <div>
            <h2>Внимание!</h2>
            <div>Необходимо отменить все активные подписки</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

WardsList.propTypes = {
  fetchNextPage: PropTypes.func,
  onSortedChange: PropTypes.func,
  isLoading: PropTypes.bool,
  relations: PropTypes.instanceOf(Map).isRequired,
  canAdd: PropTypes.bool,
  defaultSorted: PropTypes.array,
  updateRelation: PropTypes.func.isRequired,
};

export default WardsList;
